import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";

import { useMemo } from "react";

import { twMerge } from "tailwind-merge";

import CustomCard from "components/CustomCard";
import { entityUrl } from "components/EntityPageLayout";
import SignUpForm from "components/SignUpForm";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import shortSecurity from "utils/shortSecurity";

import EntityPageDiscussionsList from "./EntityPageDiscussionsList";

const AccountUpgradeForm = dynamic(
  () => import("components/AccountUpgradeForm")
);

type EntityPageDiscussionsProps = {};

const EntityPageDiscussions = ({}: EntityPageDiscussionsProps) => {
  const router = useRouter();
  const slug = router.query.slug as string;

  const { authenticated, account } = useAuthentication();

  const { data: entity } = useApi(entityUrl(slug));
  const { prettyName: name, security } = entity || {};
  const shortedSecurity = shortSecurity(security);
  const pageTitleName = useMemo(() => {
    if (shortedSecurity) {
      return `${name} | ${shortedSecurity} `;
    }
    return name;
  }, [shortedSecurity, name]);
  const title = `${pageTitleName} - Discussions`;
  const description = "Sign up to view latest discussions on Smartkarma";

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      {authenticated ? (
        account?.isPreviewPass ? (
          <div className="mt-0 md:mt-[45px]">
            <AccountUpgradeForm />
          </div>
        ) : (
          <>
            <CustomCard
              className={twMerge(
                "mx-0 mt-0 rounded-t-none",
                "md:rounded-xl md:mt-[45px]",
                "mb-0 md:mb-[30px]",
                "p-[15px] md:p-[30px]",
                "min-h-screen"
              )}
            >
              {!account || account?.canViewComment ? (
                <EntityPageDiscussionsList />
              ) : (
                <i>
                  As a {account?.accountType} holder, you do not have access to
                  discussions
                </i>
              )}
            </CustomCard>
          </>
        )
      ) : (
        <div className="md:mt-[45px] md:shadow">
          <SignUpForm entityId={entity?.id} type="insight-signup" />
        </div>
      )}
    </>
  );
};

export default EntityPageDiscussions;
